import clsx from 'clsx'

// ---------------------------------------------------------------------------
// BarChart
//
// Dependency-free stacked bar chart built from plain <div>s. Styling is
// delegated entirely to the .bars / .bar-col / .bar / .bar--alt / .bars-labels
// family in shared/styles/components.css (token-backed, ported from the
// prototype). The only inline style is each segment's percentage height —
// the proportional value is data, not a color or a styling decision, so it
// cannot live in a static class.
//
// API:
//   series[] — one or more value series, drawn stacked within each column.
//     label — series name (caller supplies already-localised text)
//     data  — one number per column; data.length should match labels.length
//     tone  — 'primary' (default) | 'accent'; maps to --color-chart /
//             --color-accent via the .bar / .bar--alt classes
//   labels[]  — per-column x-axis labels (already localised)
//   legend    — when true, render a legend row with each series' summed total
//
// v1 callers pass a single series; a second series can be added later without
// a breaking change — it simply stacks on top with the accent tone.
//
// Bar heights are normalised against the tallest *stacked column total* so a
// fully-stacked column reaches 100% of the track, matching the prototype.
// ---------------------------------------------------------------------------

export type BarChartTone = 'primary' | 'accent'

export type BarChartSeries = {
  /** Series name — caller supplies already-localised text. */
  label: string
  /** One value per column. Shorter/longer than labels is tolerated (missing → 0). */
  data: number[]
  /** Segment color tone. Defaults to 'primary' (the first series). */
  tone?: BarChartTone
}

export type BarChartProps = {
  /** Value series, drawn stacked within each column. At least one expected. */
  series: BarChartSeries[]
  /** Per-column x-axis labels. */
  labels: string[]
  /** Render a legend row with a summed total per series. */
  legend?: boolean
  /** Optional extra class on the root wrapper. */
  className?: string
  /** Accessible name for the chart region. */
  'aria-label'?: string
}

const TONE_CLASS: Record<BarChartTone, string> = {
  // The first/base series uses the plain .bar (primary); a stacked series uses
  // .bar--alt (accent). Both colors come from tokens via those classes.
  primary: 'bar',
  accent: 'bar bar--alt',
}

/** Sum of a numeric array (empty → 0). */
function sum(values: number[]): number {
  return values.reduce((total, value) => total + value, 0)
}

/**
 * Stacked bar chart. Presentational — no data fetching, no auth logic.
 * Safe for the public sessionless register bundle.
 *
 * Usage:
 *   <BarChart
 *     series={[{ label: t('…legend'), data: applications }]}
 *     labels={months}
 *     legend
 *   />
 */
export function BarChart({
  series,
  labels,
  legend = false,
  className,
  'aria-label': ariaLabel,
}: BarChartProps) {
  const columnCount = labels.length

  // Per-column stacked total → the normalisation denominator is the tallest
  // column. Guard against an all-zero/empty dataset so we never divide by 0.
  const columnTotals = Array.from({ length: columnCount }, (_, column) =>
    sum(series.map((s) => s.data[column] ?? 0)),
  )
  const maxColumnTotal = columnTotals.reduce((max, total) => Math.max(max, total), 0)
  const scale = maxColumnTotal > 0 ? maxColumnTotal : 1

  return (
    <div className={clsx('chart', className)}>
      {legend && (
        <div className="chart__legend">
          {series.map((s) => (
            <span key={s.label} className="chart__legend-item">
              <span
                className={clsx('chart__legend-swatch', {
                  'chart__legend-swatch--alt': (s.tone ?? 'primary') === 'accent',
                })}
                aria-hidden="true"
              />
              {s.label} <strong>{sum(s.data)}</strong>
            </span>
          ))}
        </div>
      )}

      <div className="bars" role="img" aria-label={ariaLabel}>
        {Array.from({ length: columnCount }, (_, column) => (
          <div key={column} className="bar-col">
            {/* Render later series first so the base (primary) series sits at
                the bottom of the stacked column, matching the prototype. */}
            {series
              .map((s, seriesIndex) => ({ s, seriesIndex }))
              .reverse()
              .map(({ s, seriesIndex }) => {
                const value = s.data[column] ?? 0
                const tone = s.tone ?? 'primary'
                return (
                  <div
                    key={seriesIndex}
                    className={TONE_CLASS[tone]}
                    style={{ height: `${(value / scale) * 100}%` }}
                  />
                )
              })}
          </div>
        ))}
      </div>

      <div className="bars-labels">
        {labels.map((label, column) => (
          <span key={column}>{label}</span>
        ))}
      </div>
    </div>
  )
}
